import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { RankAvatarBadge } from '../components/RankAvatarBadge';
import { DmThread } from '../components/core/dm/DmThread';
import { dayLabel } from '../components/core/chat/chatUtils';
import { getConversations, createConversation, getOwnerInfo } from '../services/api';
import { MessageSquare, Loader2, Plus, Search, Shield } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const previewOf = (msg) => {
  if (!msg) return 'No messages yet';
  if (msg.deleted) return 'Message deleted';
  if (msg.imageUrl && !msg.content) return '📷 Photo';
  return msg.content;
};

const MessagesPage = () => {
  const { currentUser, isOwner, isModerator } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadConversations = useCallback(async () => {
    try { setLoading(true); setConversations(await getConversations()); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadConversations();
    if (!isOwner) getOwnerInfo().then(setOwnerInfo).catch(() => {});
  }, [isOwner, loadConversations]);

  // Keep unread badges fresh while browsing the list
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      getConversations().then((data) => setConversations(data.map((c) => (c.id === selected?.id ? { ...c, unreadCount: 0 } : c)))).catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, [selected?.id]);

  const openConversation = (conv) => {
    setSelected(conv);
    setConversations((prev) => prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0 } : c)));
  };

  const startWith = async (userId) => {
    try {
      const conv = await createConversation(userId);
      setConversations((prev) => (prev.some((c) => c.id === conv.id) ? prev : [conv, ...prev]));
      openConversation(conv);
      setMemberSearch(''); setSearchResults([]);
    } catch { toast({ title: 'Error', description: 'Failed to start conversation', variant: 'destructive' }); }
  };

  const handleSearchMembers = async (query) => {
    setMemberSearch(query);
    if (query.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      const q = query.toLowerCase();
      setSearchResults(response.data.filter((u) => u.id !== currentUser.id && (u.displayName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))).slice(0, 5));
    } catch { /* silent */ }
    finally { setSearchLoading(false); }
  };

  const onMessageSent = (sent) => {
    setConversations((prev) => {
      const next = prev.map((c) => (c.id === sent.conversationId ? { ...c, lastMessage: sent, lastMessageAt: sent.createdAt } : c));
      return next.sort((a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0));
    });
  };

  const list = (
    <div className="flex flex-col h-full min-h-0 bg-[#18181b] md:border-r md:border-zinc-800/80" data-testid="dm-conversation-list">
      <div className="px-4 py-3 border-b border-zinc-800/80 shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-amber-500" />
          <h1 className="text-lg font-headline text-zinc-100 tracking-wider">CASE COMMUNICATIONS</h1>
        </div>
        <p className="text-xs text-zinc-500 mt-0.5 font-typewriter">{isOwner ? 'Private lines from investigators' : 'Direct line to the detective'}</p>
      </div>

      {!isOwner && ownerInfo && (
        <div className="p-3 border-b border-zinc-800/80 space-y-2 shrink-0">
          <button onClick={() => startWith(ownerInfo.id)} className="w-full h-9 flex items-center justify-center gap-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-black text-xs font-headline tracking-wider transition-colors" data-testid="start-conversation-btn">
            <Plus className="w-4 h-4" /> MESSAGE {ownerInfo.displayName?.toUpperCase() || 'THE CREATOR'}
          </button>
          {ownerInfo.moderator && (
            <button onClick={() => startWith(ownerInfo.moderator.id)} className="w-full h-9 flex items-center justify-center gap-2 rounded-lg border border-zinc-700 hover:border-amber-500/60 text-amber-500 text-xs font-headline tracking-wider transition-colors" data-testid="start-moderator-conversation-btn">
              <Shield className="w-4 h-4" /> MESSAGE {ownerInfo.moderator.displayName?.toUpperCase() || 'MODERATOR'}
            </button>
          )}
        </div>
      )}

      {isOwner && (
        <div className="p-3 border-b border-zinc-800/80 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input value={memberSearch} onChange={(e) => handleSearchMembers(e.target.value)} placeholder="Search members to message…"
              className="pl-9 h-9 bg-[#121215] border-zinc-700/80 focus:border-amber-500 font-typewriter text-sm" data-testid="member-search-input" />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-2 bg-[#121215] border border-zinc-700/80 rounded-lg overflow-hidden">
              {searchResults.map((m) => (
                <button key={m.id} onClick={() => startWith(m.id)} className="w-full p-2.5 hover:bg-zinc-800 flex items-center gap-3 border-b border-zinc-800 last:border-0 text-left" data-testid={`member-result-${m.id}`}>
                  <Avatar className="w-7 h-7 border border-zinc-700"><AvatarImage src={m.avatarUrl} /><AvatarFallback className="bg-zinc-800 text-amber-500 text-xs">{m.displayName?.charAt(0) || '?'}</AvatarFallback></Avatar>
                  <div className="min-w-0"><p className="text-sm text-zinc-100 font-headline truncate">{m.displayName}</p><p className="text-[11px] text-zinc-500 font-typewriter truncate">{m.email}</p></div>
                </button>
              ))}
            </div>
          )}
          {searchLoading && <div className="mt-2 flex justify-center py-1"><Loader2 className="w-4 h-4 text-amber-500 animate-spin" /></div>}
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>
        ) : conversations.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 font-typewriter">
            <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No communications yet</p>
            {!isOwner && <p className="text-xs text-zinc-600 mt-1">Start a conversation above</p>}
          </div>
        ) : conversations.map((conv) => {
          const active = selected?.id === conv.id;
          return (
            <button key={conv.id} onClick={() => openConversation(conv)} data-testid={`conversation-${conv.id}`}
              className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-zinc-800/60 transition-colors ${active ? 'bg-zinc-800/70 border-l-2 border-l-amber-500' : 'hover:bg-zinc-800/40 border-l-2 border-l-transparent'}`}>
              <div className="relative flex-shrink-0">
                <Avatar className="w-10 h-10 border border-zinc-700/80"><AvatarImage src={conv.otherUser?.avatarUrl} /><AvatarFallback className="bg-zinc-800 text-amber-500">{conv.otherUser?.displayName?.charAt(0) || '?'}</AvatarFallback></Avatar>
                <RankAvatarBadge rank={conv.otherUser?.rank} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`font-headline tracking-wide truncate ${conv.otherUser?.role === 'owner' ? 'text-red-400' : 'text-zinc-100'}`}>{conv.otherUser?.displayName || 'Unknown'}</p>
                  {conv.otherUser?.role === 'owner' && <span className="bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded tracking-widest uppercase">Daria</span>}
                  <span className="ml-auto text-[10px] font-typewriter text-zinc-500 shrink-0">{dayLabel(conv.lastMessageAt || conv.lastMessage?.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className={`text-xs truncate font-typewriter ${conv.unreadCount > 0 ? 'text-zinc-200' : 'text-zinc-500'}`}>{previewOf(conv.lastMessage)}</p>
                  {conv.unreadCount > 0 && <span className="ml-auto bg-amber-600 text-black font-bold text-[10px] px-1.5 rounded-full leading-4 shrink-0" data-testid={`unread-${conv.id}`}>{conv.unreadCount}</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const thread = selected ? (
    <DmThread key={selected.id} conversation={selected} me={currentUser} canModerate={isOwner || isModerator} showBack={isMobile} onBack={() => setSelected(null)} onMessageSent={onMessageSent} />
  ) : (
    <div className="h-full flex items-center justify-center bg-[#121215]">
      <div className="text-center text-zinc-500 font-typewriter">
        <MessageSquare className="w-14 h-14 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Select a conversation to view communications</p>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <motion.div className="bg-black h-[calc(100dvh-4rem)] overflow-hidden" data-testid="messages-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        {selected ? thread : list}
      </motion.div>
    );
  }

  return (
    <motion.div className="min-h-[calc(100dvh-4rem)] bg-black py-6" data-testid="messages-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid grid-cols-[minmax(260px,1fr)_2fr] rounded-xl border border-zinc-800 overflow-hidden shadow-2xl h-[calc(100dvh-7rem)]">
          {list}
          {thread}
        </div>
      </div>
    </motion.div>
  );
};

export default MessagesPage;
